import { FC } from 'react';
import { useNavigate } from 'react-router';

import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { Delete, Dropdown, Maintenance, Options, Pencil, Regular, Shadows, Tabs, useModal } from '@zazume/zzm-base';
import { formatDate } from '@zazume/zzm-utils';

import { GenericDeleteConfirmationModal } from '../../../../../components/modal/GenericDeleteConfirmationModal';
import { PageDetailTitle } from '../../../../../components/pages/detail/PageDetailTitle';
import { Can } from '../../../../../components/roles/Can';
import { Private } from '../../../../../containers/router/routes/Private';
import { useDelete, useNotifications } from '../../../../../hooks';
import { ProvidersKeys } from '../../../../../hooks/api/providers/Providers.keys';
import { useURLTabs } from '../../../../../hooks/router/useURLTabs';
import { useI18n } from '../../../../../hooks/useI18n';
import { API } from '../../../../../lib/network/API';
import { isAClientError, useNetworkExceptions } from '../../../../../lib/network/helpers';
import { Provider } from '../../../../../models';
import { EditProviderModal } from '../modals/EditProvider.modal';
import { AboutTab } from './AboutTab';
import { DocumentsTab } from './DocumentsTab';

const Container = styled.div(
  ({ theme }) => css`
    width: 100%;
    padding: ${theme.spacing.s10};
    box-shadow: ${Shadows.SHADOW_3};
    background: ${theme.colors.White};
    border-radius: ${theme.radius.xl};

    ${theme.mq.desktop} {
      padding: ${theme.spacing.s13} ${theme.spacing.s16};
    }
  `
);

interface ProviderDetailContentProps {
  provider: Provider;
}

export const ProviderDetailContent: FC<ProviderDetailContentProps> = ({ provider }) => {
  const { t } = useI18n();
  const { handleNetworkExceptions } = useNetworkExceptions();
  const { showNotification } = useNotifications();
  const navigate = useNavigate();

  const { open: openEditModal, RenderModal: RenderEditModal } = useModal({ modalSize: 'fullscreen' });
  const {
    open: showRemoveModal,
    RenderModal: RenderRemoveModal,
    close: hideRemoveModal
  } = useModal({ modalSize: 's', withMobileFullscreen: false });
  const urlTabsProps = useURLTabs();

  const queryKey = ProvidersKeys.byId(provider._id);
  const { mutate: remove } = useDelete(queryKey, API.providers.delete, { isOptimistic: false });

  const handleOnConfirmRemoveModal = async () => {
    try {
      await remove(provider._id);
      showNotification(t('providers.modals.remove.successMessage'));
      navigate(Private.PROVIDERS.route);
    } catch (error) {
      if (isAClientError(error)) {
        return handleNetworkExceptions(error);
      }
    } finally {
      hideRemoveModal();
    }
  };

  return (
    <Container>
      <PageDetailTitle
        icon={<Maintenance color="Gray600" size={32} />}
        title={provider.name}
        rightContent={
          <Can hasRoles={['buildingManager', 'admin', 'ownerManager']}>
            <Regular variant="S" color="Gray400">
              {t('providers.common.addedOn')} {formatDate(provider.createdAt, 'DD/MM/YYYY')}
            </Regular>
            <Dropdown header={<Options size={24} cursor="pointer" color="Gray600" />} position="right" topValue={30}>
              <Can hasRoles={['buildingManager', 'admin', 'ownerManager']}>
                <Dropdown.Item onClick={openEditModal} icon="pencil">
                  {t('providers.table.options.edit')}
                </Dropdown.Item>
              </Can>
              <Dropdown.Item onClick={showRemoveModal} icon="delete" variant="danger">
                {t('general.remove')}
              </Dropdown.Item>
            </Dropdown>
          </Can>
        }
      />
      <Tabs label={t('ownerAccount.detail.title')} {...urlTabsProps}>
        <Tabs.Tab key="about" title={t('general.about')}>
          <AboutTab provider={provider} />
        </Tabs.Tab>
        <Tabs.Tab key="documents" title={t('general.docs')}>
          <DocumentsTab providerId={provider._id} />
        </Tabs.Tab>
      </Tabs>
      <RenderEditModal>
        <EditProviderModal provider={provider} queryKey={queryKey} />
      </RenderEditModal>
      <RenderRemoveModal>
        <GenericDeleteConfirmationModal
          title={t('providers.modals.remove.title')}
          description={t('providers.modals.remove.description')}
          onCancel={hideRemoveModal}
          onConfirm={handleOnConfirmRemoveModal}
        />
      </RenderRemoveModal>
    </Container>
  );
};
